import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import {
  History,
  Warehouse,
  User,
  Clock,
  TrendingUp,
  TrendingDown,
  BarChart3,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import clientService from '../services/clientService';

/**
 * ClientInventoryManager Component
 * Shows inventory change audit history and remaining stock summary
 */
const ClientInventoryManager = ({
  productId,
  productInfo
}) => {
  const [auditHistory, setAuditHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const loadAuditHistory = useCallback(async (pageNum = 0, pageSize = 10) => {
    if (!productId) return;

    setLoading(true);
    setError('');

    try {
      const response = await clientService.getInventoryAuditHistory(
        productId,
        pageNum + 1, // API uses 1-based pagination
        pageSize
      );

      if (response.success) {
        setAuditHistory(response.audit_records || []);
        setSummary(response.summary || {});
        setTotalCount(response.total_count || 0);
      } else {
        setError('Failed to load audit history');
      }
    } catch (err) {
      console.error('Failed to load audit history:', err);
      setError(err.response?.data?.error || 'Failed to load audit history');
      setAuditHistory([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    if (productId) {
      loadAuditHistory(page, rowsPerPage);
    }
  }, [productId, page, rowsPerPage, loadAuditHistory]);

  const handleChangePage = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((value) => {
    const newRowsPerPage = parseInt(value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  }, []);

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    try {
      return new Date(timestamp).toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return timestamp;
    }
  };

  const getChangeIcon = (quantityChange) => {
    if (quantityChange > 0) {
      return <TrendingUp className="h-4 w-4 text-success" />;
    } else if (quantityChange < 0) {
      return <TrendingDown className="h-4 w-4 text-destructive" />;
    }
    return <BarChart3 className="h-4 w-4 text-muted-foreground" />;
  };

  const getChangeTypeDisplay = (changeType) => {
    if (changeType.includes('client_inventory_update')) {
      return '客户库存更新';
    } else if (changeType.includes('client_inventory_delete')) {
      return '客户库存删除';
    } else {
      return changeType.replace('_', ' ');
    }
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const startItem = page * rowsPerPage + 1;
  const endItem = Math.min((page + 1) * rowsPerPage, totalCount);

  if (!productId) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">
          请先选择产品以查看库存变更记录
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-2 mb-6">
        <History className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">
          库存变更记录
        </h3>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription className="flex items-center justify-between">
            {error}
            <Button variant="ghost" size="sm" onClick={() => setError('')}>×</Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Section */}
      {summary && (
        <Card className="mb-6 shadow-md">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <Warehouse className="h-5 w-5 text-primary" />
                <span className="text-sm text-muted-foreground">
                  当前库存:
                </span>
                <Badge
                  variant={summary.current_stock > 0 ? 'default' : 'destructive'}
                  className={summary.current_stock > 0 ? 'bg-success/100' : ''}
                >
                  {summary.current_stock || 0}
                </Badge>
              </div>

              <Separator orientation="vertical" className="h-6" />

              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  总变更次数:
                </span>
                <Badge variant="outline">
                  {summary.total_changes || 0}
                </Badge>
              </div>

              {productInfo?.name && (
                <>
                  <Separator orientation="vertical" className="h-6" />
                  <div>
                    <span className="text-sm text-muted-foreground">
                      产品名称: <strong>{productInfo.name}</strong>
                    </span>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Audit History Table */}
      {loading ? (
        <Card className="border border-gray-200">
          <CardContent className="p-4">
            <div className="space-y-4">
              {[...Array(5)].map((_, index) => (
                <div key={index} className="h-14 bg-muted/80 animate-pulse rounded" />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : auditHistory.length > 0 ? (
        <Card className="border border-gray-200">
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">时间</th>
                  <th className="px-4 py-3 text-left font-semibold">操作类型</th>
                  <th className="px-4 py-3 text-left font-semibold">客户</th>
                  <th className="px-4 py-3 text-center font-semibold">原数量</th>
                  <th className="px-4 py-3 text-center font-semibold">新数量</th>
                  <th className="px-4 py-3 text-center font-semibold">变更量</th>
                  <th className="px-4 py-3 text-left font-semibold">操作员</th>
                  <th className="px-4 py-3 text-left font-semibold">备注</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {auditHistory.map((record, index) => (
                  <tr key={record.id || index} className="hover:bg-muted">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span>{formatDateTime(record.timestamp)}</span>
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <Badge variant="outline">
                        {getChangeTypeDisplay(record.change_type)}
                      </Badge>
                    </td>

                    <td className="px-4 py-3">
                      {record.client_name ? (
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span>{record.client_name}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>

                    <td className="px-4 py-3 text-center text-muted-foreground">
                      {record.previous_quantity}
                    </td>

                    <td className="px-4 py-3 text-center font-medium">
                      {record.new_quantity}
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        {getChangeIcon(record.quantity_change)}
                        <span
                          className={`font-medium ${
                            record.quantity_change > 0 ? 'text-success' :
                            record.quantity_change < 0 ? 'text-destructive' : 'text-muted-foreground'
                          }`}
                        >
                          {record.quantity_change > 0 ? '+' : ''}{record.quantity_change}
                        </span>
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      {record.user || 'unknown'}
                    </td>

                    <td className="px-4 py-3">
                      <span
                        className="text-muted-foreground max-w-[200px] truncate block"
                        title={record.note || record.reason}
                      >
                        {record.note || record.reason || '-'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">每页行数:</span>
              <Select value={String(rowsPerPage)} onValueChange={handleChangeRowsPerPage}>
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[5, 10, 20, 50].map((size) => (
                    <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                {startItem}-{endItem} of {totalCount}
              </span>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handleChangePage(page - 1)}
                  disabled={page === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handleChangePage(page + 1)}
                  disabled={page >= totalPages - 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="border border-dashed border-gray-300 text-center py-12">
          <CardContent>
            <History className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-muted-foreground mb-2">
              暂无变更记录
            </h4>
            <p className="text-sm text-muted-foreground">
              该产品尚未有任何库存变更操作
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientInventoryManager;
